import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Shield, Users, Check, X, FileText, Mail, CreditCard, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Navigation from "@/components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [stories, setStories] = useState([]);
  const [streetStories, setStreetStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");

  useEffect(() => {
    if (!authLoading && (!user || user.role !== "admin")) {
      navigate("/login");
      return;
    }
    if (user && user.role === "admin") {
      loadData();
    }
  }, [user, authLoading]);

  const loadData = async () => {
    try {
      const [statsRes, storiesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/stories`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setStories(storiesRes.data.stories || []);
      setStreetStories(storiesRes.data.street_stories || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (storyId, action) => {
    try {
      await axios.post(`${API}/admin/stories/${storyId}/${action}`, {}, { withCredentials: true });
      toast.success(`Story ${action}ed`);
      loadData();
    } catch {
      toast.error("Failed to update story");
    }
  };

  const allStories = [...stories, ...streetStories];
  const filtered = tab === "pending" ? allStories.filter(s => s.status === "pending") : tab === "approved" ? allStories.filter(s => s.status === "approved") : allStories;

  if (authLoading || loading) return (
    <div className="min-h-screen bg-[#FDFBF7]"><Navigation /><div className="pt-28 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#4FB3D4]" /></div></div>
  );

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />
      <section className="pt-28 pb-20 px-6 lg:px-12" data-testid="admin-section">
        <div className="container mx-auto max-w-5xl">
          <div className="flex items-center gap-3 mb-8">
            <Shield className="h-6 w-6 text-[#A69BE0]" />
            <h1 className="font-heading text-2xl font-bold text-[#1A2A3A]" data-testid="admin-title">Admin Dashboard</h1>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
              {[
                { label: "Users", value: stats.users, icon: Users, color: "text-[#4FB3D4]" },
                { label: "Pending", value: stats.stories_pending, icon: FileText, color: "text-amber-500" },
                { label: "Approved", value: stats.stories_approved, icon: Check, color: "text-green-500" },
                { label: "Payments", value: stats.payments, icon: CreditCard, color: "text-[#A69BE0]" },
                { label: "Emails", value: stats.emails_sent, icon: Mail, color: "text-[#FFB7B2]" },
              ].map((s, i) => (
                <Card key={i} className="bg-white rounded-xl shadow-sm border border-[#E5E7EB]/50 p-4" data-testid={`admin-stat-${i}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <s.icon className={`h-4 w-4 ${s.color}`} />
                    <span className="text-xs text-[#1A2A3A]/50">{s.label}</span>
                  </div>
                  <p className="font-heading text-2xl font-bold text-[#1A2A3A]">{s.value}</p>
                </Card>
              ))}
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            {["pending", "approved", "all"].map(t => (
              <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${tab === t ? "bg-[#4FB3D4] text-white" : "bg-white text-[#1A2A3A]/60 border border-[#E5E7EB]"}`} data-testid={`admin-tab-${t}`}>
                {t.charAt(0).toUpperCase() + t.slice(1)} ({t === "pending" ? allStories.filter(s => s.status === "pending").length : t === "approved" ? allStories.filter(s => s.status === "approved").length : allStories.length})
              </button>
            ))}
          </div>

          {/* Stories List */}
          <div className="space-y-3">
            {filtered.length === 0 ? (
              <p className="text-sm text-[#1A2A3A]/50 text-center py-8">No stories in this category.</p>
            ) : (
              filtered.map((s, i) => (
                <Card key={i} className="bg-white rounded-xl shadow-sm border border-[#E5E7EB]/50" data-testid={`admin-story-${i}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                          <span className="text-sm font-semibold text-[#1A2A3A]">{s.name || "Anonymous"}</span>
                          {s.location && <span className="text-[10px] text-[#1A2A3A]/40">{s.location}</span>}
                          {s.relationship_type && <span className="text-[10px] bg-[#A69BE0]/10 text-[#A69BE0] px-2 py-0.5 rounded-full">{s.relationship_type}</span>}
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.status === "approved" ? "bg-green-50 text-green-600" : s.status === "pending" ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-600"}`}>
                            {s.status}
                          </span>
                        </div>
                        <p className="text-sm text-[#1A2A3A]/60 line-clamp-2">{s.story}</p>
                        <p className="text-[10px] text-[#1A2A3A]/30 mt-1">{s.created_at}</p>
                      </div>
                      {s.status === "pending" && (
                        <div className="flex gap-1.5 flex-shrink-0">
                          <Button size="sm" onClick={() => handleAction(s.id, "approve")} className="bg-green-500 hover:bg-green-600 text-white rounded-lg px-3 py-1.5 h-8" data-testid={`approve-${s.id}`}>
                            <Check className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" onClick={() => handleAction(s.id, "reject")} variant="outline" className="border-red-200 text-red-500 hover:bg-red-50 rounded-lg px-3 py-1.5 h-8" data-testid={`reject-${s.id}`}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminPage;
